<template>
  <div class="form-container">
    <Loader :isVisible="isLoading && showLoading" />
    <div class="collection-header">
      <div class="left-path">
        <span class="system">
          Collections / Series
        </span>
        <span>
          / Edit {{ series && series.title }} / Edit {{ metadata.title }}
        </span>
      </div>
      <div class="edit-button-wrapper">
        <Button
          title="Delete Season"
          classType="transparent"
          :eventFunction="handleOpenDeleteModal"
        />
        <Button
          title="Back"
          classType="warning"
          :eventFunction="handleBackToSeasons"
        />
      </div>
    </div>

    <div class="title">
      Season Editor
    </div>
    <div>
      <Tabs>
        <Tab
          id="general"
          v-slot="{ hasWarning, setWarning, shouldShowWarning, resetShouldShowWarning }"
          name="General"
          :selected="mode === 'general'"
          :className="['tab']"
        >
          <div
            v-if="isMetadataInited"
            class="wrapper main-form-container"
          >
            <div class="column column-1">
              <CollectionGeneralForm
                collectionType="season"
                :metadata="metadata"
                :enabledLanguages="enabledLanguages"
                :isCategoryManagementAvailabe="true"
                :isGenreManagementAvailabe="true"
                :hasChanges="hasWarning"
                :setHasChanges="setWarning"
                :shouldShowWarning="shouldShowWarning"
                :submitEventName="submitEventName"
                :submitParams="submitParams"
                :successMessageText="successMessage"
                @handleUpdatedMetadata="handleUpdatedMetadata"
                @reloadItem="handleReloadItem"
                @resetShouldShowWarning="resetShouldShowWarning"
              />
            </div>
          </div>
        </Tab>
        <Tab
          id="Images"
          v-slot="{ hasWarning, setWarning, shouldShowWarning, resetShouldShowWarning }"
          name="Images"
          :selected="mode === 'images'"
          :className="['tab']"
        >
          <div
            v-if="isMetadataInited"
            class="wrapper main-form-container"
          >
            <div class="column column-1">
              <CollectionImagesForm
                :metadata="metadata"
                :enabledLanguages="enabledLanguages"
                :hasChanges="hasWarning"
                :setHasChanges="setWarning"
                :shouldShowWarning="shouldShowWarning"
                :submitEventName="submitEventName"
                :submitParams="submitParams"
                collectionType="season"
                @reloadItem="handleReloadItem"
                @resetShouldShowWarning="resetShouldShowWarning"
              />
            </div>
          </div>
        </Tab>
        <Tab
          id="dataSource"
          v-slot="{ hasWarning, setWarning, shouldShowWarning, resetShouldShowWarning }"
          name="Data Source"
          :selected="mode === 'dataSource'"
          :className="['tab']"
        >
          <CollectionDataSourceForm
            v-if="isMetadataInited"
            :metadata="metadata"
            :submitEventName="submitEventName"
            :submitParams="submitParams"
            :successMessageText="successMessage"
            :hasChanges="hasWarning"
            :setHasChanges="setWarning"
            :shouldShowWarning="shouldShowWarning"
            @handleUpdatedMetadata="handleUpdatedMetadata"
            @reloadItem="handleReloadItem"
            @resetShouldShowWarning="resetShouldShowWarning"
          />
        </Tab>
        <Tab
          v-if="isMetadataInited &&isAccountHasSubscriptionProvider"
          id="access"
          v-slot="{ hasWarning, setWarning, shouldShowWarning, resetShouldShowWarning }"
          name="Access"
          :selected="mode === 'access'"
          :className="['tab']"
        >
          <CollectionAccessForm
            collectionType="Season"
            :metadata="metadata"
            :submitEventName="submitEventName"
            :submitParams="submitParams"
            :successMessageText="successMessage"
            :hasChanges="hasWarning"
            :setHasChanges="setWarning"
            :shouldShowWarning="shouldShowWarning"
            @handleUpdatedMetadata="handleUpdatedMetadata"
            @reloadItem="handleReloadItem"
            @resetShouldShowWarning="resetShouldShowWarning"
          />
        </Tab>
      </Tabs>
      <NotificationModal
        ref="modal"
        v-model="isDeleteModalOpen"
        :modal="isDeleteModalOpen"
        :bodyMessage="deleteBodyMessage"
        title="Delete Season"
        :warningMess="deleteWarningMess"
        :isInUseAsset="true"
        :onAccept="handleDeleteSeason"
        :onClose="handleCloseDeleteModal"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import CONFIG from '../../../constants/config';
import {
  SET_EDIT_FORM,
  SET_OVERFLOW,
} from '../../../store/actions/common';

import Loader from '../../common/Loader.vue';
import Tabs from '../../common/Tabs/Tabs.vue';
import Tab from '../../common/Tabs/Tab.vue';
import Button from '../../common/Button.vue';
import NotificationModal from '../../NotificationModal.vue';
import {
  GET_LANGUAGES_REQUEST,
} from '../../../store/actions/localization/localizationActions';
import {
  DELETE_SEASONS_REQUEST,
  PATCH_SEASONS_REQUEST,
  GET_SEASONS_REQUEST,
} from '../../../store/actions/seasons/seasons';
import CollectionGeneralForm from '../CollectionGeneralForm.vue';
import CollectionImagesForm from '../CollectionImagesForm.vue';
import CollectionDataSourceForm from '../CollectionDataSourceForm.vue';
import CollectionAccessForm from '../CollectionAccessForm.vue';

export default {
  name: 'SeasonEditForm',
  components: {
    CollectionGeneralForm,
    CollectionImagesForm,
    CollectionDataSourceForm,
    CollectionAccessForm,
    NotificationModal,
    Tab,
    Tabs,
    Loader,
    Button,
  },
  props: {
    formPrefix: {
      type: String,
      default: 'playlistEditForm',
    },
  },
  data() {
    return {
      deleteWarningMess: 'Deleting a Season will not result in deleting the Source Folder and its Media Items',
      deleteBodyMessage: CONFIG.modals.deleteSeasonMessage,
      isDeleteModalOpen: false,
      mode: 'general',
      enabledLanguages: [],
      season: {},
      isMetadataInited: false,
      metadata: {},
      submitEventName: PATCH_SEASONS_REQUEST,
      successMessage: CONFIG.successMessages.editSeasonsMessage,
      showLoading: true,
    };
  },
  computed: {
    ...mapGetters([
      'getCurrentAccountId',
      'isAccountHasSubscriptionProvider',
    ]),
    ...mapState({
      isLoading: (state) => state.seasons.isLoading,
      isEditedForm: (state) => state.common.isEditedForm,
      series: (state) => state.series.series,
    }),
    requestParams() {
      return {
        accountId: this.getCurrentAccountId,
        seasonsId: this.$route.params.seasonId,
      };
    },
    submitParams() {
      return {
        seasonsId: this.$route.params.seasonId,
        accountId: this.getCurrentAccountId,
        data: {},
      };
    },
  },
  beforeMount() {
    window.addEventListener('beforeunload', this.preventNav);
  },
  mounted() {
    this.loadItemData();

    this.$store.dispatch(GET_LANGUAGES_REQUEST, this.requestParams).then((res) => {
      this.enabledLanguages = res.data;
    });
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.preventNav);
  },
  beforeRouteLeave(to, from, next) {
    if (this.isEditedForm) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(CONFIG.confirmationLeaveMessage)) {
        this.$router.push(this.$route.path);
        return;
      }
      this.$store.dispatch(SET_EDIT_FORM, false);
    }
    next();
  },
  methods: {
    handleReloadItem() {
      this.showLoading = false;
      this.loadItemData(true);
    },
    loadItemData(isSilent = false) {
      if (!isSilent) {
        this.isMetadataInited = false;
      }
      this.$store.dispatch(GET_SEASONS_REQUEST, this.requestParams).then((res) => {
        this.season = res;
        this.initFormData(res);
        this.showLoading = true;
      });
    },
    initFormData(metadata) {
      this.metadata = metadata;
      this.isMetadataInited = true;
    },
    handleUpdatedMetadata(metadata) {
      this.initFormData(metadata);
    },
    handleBackToSeasons() {
      this.$router.push(`/collections/series/${this.$route.params.id}`);
    },
    preventNav(event) {
      if (this.isEditedForm) {
        event.returnValue = CONFIG.confirmationLeaveMessage;
      }
    },
    handleOpenDeleteModal() {
      this.isDeleteModalOpen = true;
      this.$store.dispatch(SET_OVERFLOW, true);
    },
    handleCloseDeleteModal() {
      this.isDeleteModalOpen = false;
      this.$store.dispatch(SET_EDIT_FORM, false);
    },
    handleDeleteSeason() {
      const params = this.requestParams;
      this.$store.dispatch(DELETE_SEASONS_REQUEST, params)
        .then(() => {
          this.$toasted.global.success({
            message: 'The Season has been successfully deleted',
          });
          this.handleBackToSeasons();
        })
        .catch((err) => {
          if (err.status === 400 && err.error === 'ITEM_HAS_DEPENDENCIES') {
            this.$toasted.global.error({
              message: 'This Season is in use by Video Apps Engine and cannot be deleted',
            });
          } else {
            this.$toasted.global.error({
              message: CONFIG.errorMessages.commonServerError,
            });
          }
        });
    },
  },
};
</script>

<style scoped>
.collection-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  position: relative;
}

.system {
  color: #4c5057;
}

.form-container {
  padding: 25px 30px;
  overflow-y: auto;
  width: 100%;
}

.wrapper {
  display: flex;
}

.edit-button-wrapper {
  display: flex;
}
</style>
